import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";

import { CellStyle, ColDef, RowClickedEvent, RowDoubleClickedEvent } from 'ag-grid-community';
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { CommonCodeColumn } from "src/utils/grid/GridColumnContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig } from "src/utils/grid/gridStyleUtils";
import { defaultKSK2011_R01DetailDto, KSK2011_R01DetailDto } from "./Dto_KSK2011_R01";
import { useKSK2011_R01Store } from "./Store_KSK2011_R01";
import { headerNames_KSK2011_R01Detail } from './Util_KSK2011_R01';
import { onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";

/**
 * 사용자 관리 테이블 컴포넌트
 * 사용자 정보를 표시하고 관리하는 테이블
 */

interface Props {
	// hoveredRowIndex: number | null;
	// setHoveredRowIndex: (hoveredRowIndex: number | null) => void;
	gridRef: React.RefObject<AgGridReact<KSK2011_R01DetailDto> | null>;
}

const COLUMN_STATE_KEY = 'KSK2011_R01-detail-grid-column-state';

export const DetailTable_KSK2011_R01 = (props: Props) => {

	const { 
		gridRef,
	} = props;

    const { 
		editKSK2011_R01,
		KSK2011_R01DetailList, setKSK2011_R01DetailList,
		insertKSK2011_R01DetailList,
		removeKSK2011_R01DetailList,
		updateKSK2011_R01DetailList,
		selectedKSK2011_R01DetailList, setSelectedKSK2011_R01DetailList, initSelectedKSK2011_R01DetailList,
		deletedKSK2011_R01DetailList, removeDeletedKSK2011_R01DetailList, addDeletedKSK2011_R01DetailList,
        detailColumnState, setDetailColumnState,
		originalKSK2011_R01DetailList, setOriginalKSK2011_R01DetailList,
		modalVisible, setModalVisible,
		isDisabled,
	} = useKSK2011_R01Store();

    const { compCdList, jobTeamList, orgCdList, ordTypeList, workProcessList, workPlaceList, unitList } = useCommonCodeStore();

	// const gridWrapperRef = useRef<HTMLDivElement>(null); // 1. div를 위한 ref 생성
	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

    //const gridRef = useRef<AgGridReact>(null);

	const { 
		getRowId,
		settingRowIndex, 
		settingSecondColumnCount,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
		defaultRowIndexCellButton,
		rowIndexCellButtonType1,
		rowIndexCellButtonType2,
		handleSwitchButtonHandler,
		ensureSelectedRow,
	} = useGridSettingContent<KSK2011_R01DetailDto>({
		gridRef,
		key: 'childItemcd',
		gridData: KSK2011_R01DetailList,
		defaultGridData: defaultKSK2011_R01DetailDto,
		selectedRow: selectedKSK2011_R01DetailList,
	});

	const getCellStyle = (params: any, key: string): CellStyle => {
		return onlyTouchGridStyle(
			key,
			'childItemcd',
		)(params);
	}
	

	const baseColumnDefs: ColDef<KSK2011_R01DetailDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '품목코드', colId: 'childItemcd', field: 'childItemcd', width: 150, 
			cellStyle: (params: any) => getCellStyle(params, 'childItemcd'),
			editable: false,
		},
		{
			headerName: '품명', colId: 'childItemnm', field: 'childItemnm', width: 200,
			cellStyle: (params: any) => getCellStyle(params, 'childItemnm'),
			editable: false,
		},
		{
			headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 200,
			cellStyle: (params: any) => getCellStyle(params, 'itemSpec'),
			editable: false,
		},
		{
			headerName: '단위', colId: 'childItemUnit', field: 'childItemUnit', width: 80,
			cellStyle: (params: any) => getCellStyle(params, 'childItemUnit'),
			editable: false,
			...CommonCodeColumn.options(unitList, false, undefined, undefined),
		},
		{
			headerName: '단위소요량', colId: 'childItemUsage', field: 'childItemUsage', width: 110,
			cellStyle: (params: any) => getCellStyle(params, 'childItemUsage'),
			editable: false,
			valueFormatter: (params: any) =>
				params.value != null ? params.value.toLocaleString() : '',
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
		},
		{
			headerName: '총소요량', colId: 'totalOrderQty', field: 'totalOrderQty', width: 100,
			cellStyle: (params: any) => getCellStyle(params, 'totalOrderQty'),
			editable: false,
			valueFormatter: (params: any) =>
				params.value != null ? params.value.toLocaleString() : '',
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
		},
		{
			headerName: '고정손실량', colId: 'fixLossqty', field: 'fixLossqty', width: 110,
			cellStyle: (params: any) => getCellStyle(params, 'fixLossqty'),
			editable: false,
			valueFormatter: (params: any) =>
				params.value != null ? params.value.toLocaleString() : '',
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
		},
		{
			headerName: '손실률', colId: 'lossPercent', field: 'lossPercent', width: 90,
			cellStyle: (params: any) => getCellStyle(params, 'lossPercent'),
			editable: false,
			valueFormatter: (params: any) =>
				params.value != null ? params.value.toLocaleString() + '' : '0%',
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
		},
		{
			headerName: '자재창고', colId: 'materialWarehouseQty', field: 'materialWarehouseQty', width: 100,
			cellStyle: (params: any) => getCellStyle(params, 'materialWarehouseQty'),
			editable: false,
			valueFormatter: (params: any) =>
				params.value != null ? params.value.toLocaleString() : '',
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
		},
		{
			headerName: '재공창고', colId: 'productWarehouseQty', field: 'productWarehouseQty', width: 100,
			cellStyle: (params: any) => getCellStyle(params, 'productWarehouseQty'),
			editable: false,
			valueFormatter: (params: any) =>
				params.value != null ? params.value.toLocaleString() : '',
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
		},
		{
			headerName: '부족량', colId: 'shortageQty', field: 'shortageQty', width: 100,
			cellStyle: (params: any) => {
				const base = getCellStyle(params, 'shortageQty');
				if (Number(params.value) < 0) {
					return { ...base, backgroundColor: '#FFCCCC' }; // 부족
				}
				return base;
			},
			editable: false,
			valueFormatter: (params: any) =>
				params.value != null ? params.value.toLocaleString() : '',
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
		},

		// ...IndexParameterCell(),

	], [hoveredRowIndex, deletedKSK2011_R01DetailList, KSK2011_R01DetailList, editKSK2011_R01]);



	const columnDefs: ColDef<KSK2011_R01DetailDto>[] = useMemo(() => {
		return GridCustomColumnDefs<KSK2011_R01DetailDto>({
			baseColumnDefs,
			columnState: detailColumnState,
			headerNames: headerNames_KSK2011_R01Detail,
			fallbackHeaderClass: aggridHeaderClass
		});
	}, [baseColumnDefs, detailColumnState ]);


	const handleRowClick = useCallback((e: RowClickedEvent<KSK2011_R01DetailDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;
		
		// 선택된 표와 정보가 같은지 확인 - salesEstimateNo를 우선 사용
		const currentKey = rowData.childItemcd;
		const selectedKey = selectedKSK2011_R01DetailList?.childItemcd;
		const isSelected = selectedKey === currentKey;
		
		if (isSelected) {
			setSelectedKSK2011_R01DetailList(undefined);
		} else {
			setSelectedKSK2011_R01DetailList(rowData);
		}
	}, [selectedKSK2011_R01DetailList, setSelectedKSK2011_R01DetailList]);

	
	
	const handleRowDoubleClick = useCallback((e: RowDoubleClickedEvent<KSK2011_R01DetailDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;

		setModalVisible(true);
	}, [setModalVisible]);
	
	const pinnedBottomRow = useMemo(() => ([
		{
			rowIndex: '계',
			childItemcd: KSK2011_R01DetailList.length.toLocaleString() + "건",
			lossPercent: '',
			editable: false,
		}
	]), [KSK2011_R01DetailList]);

    return (
		<GridContextWrapper<KSK2011_R01DetailDto>
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={initSelectedKSK2011_R01DetailList}
			defaultRowData={defaultKSK2011_R01DetailDto}
			baseColumnDefs={columnDefs}
			setColumnState={setDetailColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<KSK2011_R01DetailDto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}			defaultColDef={{
					...defaultColDef

				}}
				getRowId={getRowId}
				
				rowClassRules={createRowClassRules<KSK2011_R01DetailDto>({
					idKey: 'childItemcd',
					selectedList: selectedKSK2011_R01DetailList ? [selectedKSK2011_R01DetailList.childItemcd] : [],
					deletedList: [],
					
				})}
				onRowClicked={handleRowClick}
				onRowDoubleClicked={handleRowDoubleClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}	
				
				// Grid_Select 공통 로직 처리를 위한 props 전달
				setHoveredRowIndex={setHoveredRowIndex}		//마우스 오버 상태 이벤트를 위한 핸들러
				columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 (로컬스토리)	
				setColumnState={setDetailColumnState}		//컬럼 상태 저장
			/>
		</GridContextWrapper>
	)
}